import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, CheckCheck, Award, CheckCircle2, Mic, Briefcase, Swords, Flame, Settings, Users, Video } from "lucide-react";
import api from "@/lib/api";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const TYPE_CONFIG = {
  achievement: { icon: Award, color: "text-[#FBBF24]" },
  challenge: { icon: CheckCircle2, color: "text-indigo-400" },
  interview: { icon: Mic, color: "text-purple-300" },
  recruiter: { icon: Briefcase, color: "text-emerald-400" },
  duel: { icon: Swords, color: "text-red-400" },
  reminder: { icon: Flame, color: "text-indigo-400" },
  friend: { icon: Users, color: "text-teal-400" },
  meeting: { icon: Video, color: "text-cyan-400" },
  system: { icon: Bell, color: "text-slate-400" },
};

function timeAgo(dateStr) {
  const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export default function NotificationBell() {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);

  const loadUnread = () => {
    api.get("/notifications/unread-count").then((res) => setUnreadCount(res.data.count)).catch(() => {});
  };

  useEffect(() => {
    loadUnread();
    const interval = setInterval(loadUnread, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleOpen = (isOpen) => {
    setOpen(isOpen);
    if (isOpen) {
      api.get("/notifications").then((res) => setNotifications(res.data));
    }
  };

  const markAllRead = async () => {
    await api.put("/notifications/read-all");
    setUnreadCount(0);
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  };

  return (
    <DropdownMenu open={open} onOpenChange={handleOpen}>
      <DropdownMenuTrigger asChild>
        <button className="relative focus:outline-none p-1" data-testid="notification-bell-button">
          <Bell className="w-5 h-5 text-slate-300 hover:text-white transition-colors" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full bg-red-500 text-[9px] font-bold text-white flex items-center justify-center" data-testid="notification-unread-badge">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 max-h-96 overflow-y-auto scrollbar-thin">
        <div className="flex items-center justify-between px-2 py-1.5">
          <DropdownMenuLabel className="p-0">Notifications</DropdownMenuLabel>
          <div className="flex items-center gap-3">
            {unreadCount > 0 && (
              <button onClick={markAllRead} data-testid="mark-all-read-button" className="flex items-center gap-1 text-[11px] text-indigo-400 hover:text-indigo-300">
                <CheckCheck className="w-3 h-3" /> Mark all read
              </button>
            )}
            <Link to="/profile#notification-settings" data-testid="notification-settings-link" className="text-slate-400 hover:text-white" title="Notification settings">
              <Settings className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
        <DropdownMenuSeparator />
        {notifications.length === 0 && (
          <p className="text-xs text-slate-400 text-center py-6">No notifications yet.</p>
        )}
        {notifications.map((n) => {
          const cfg = TYPE_CONFIG[n.type] || TYPE_CONFIG.system;
          const Icon = cfg.icon;
          return (
            <div key={n.id} data-testid={`notification-item-${n.id}`} className={`flex items-start gap-2.5 px-3 py-2.5 border-b border-white/5 last:border-0 ${!n.read ? "bg-indigo-400/5" : ""}`}>
              <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${cfg.color}`} />
              <div className="min-w-0">
                <p className="text-xs font-semibold text-white">{n.title}</p>
                <p className="text-xs text-slate-400 mt-0.5">{n.message}</p>
                <p className="text-[10px] text-slate-400 mt-1">{timeAgo(n.created_at)}</p>
              </div>
            </div>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
